import {
  ApiError,
  queryKeys,
  type TaskDetail,
  type TaskEvent,
  type TaskReply,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  humanizeStatus,
  Skeleton,
  TASK_STATUS_TONE,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeft, CornerDownRight, ListChecks, Pencil, Plus } from 'lucide-react'
import { useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import {
  AttachButton,
  AttachmentChips,
  AttachmentList,
  useFileUploads,
} from '../components/attachments.js'
import { LinkifiedText } from '../components/linkified-text.js'
import { MarkImportantButton } from '../components/mark-important.js'
import {
  CompleteTaskDialog,
  DueDate,
  EditTaskDialog,
  formatDueDate,
  isScheduled,
  NewTaskDialog,
  ReviewActions,
  useTaskToggle,
} from '../components/task-shared.js'
import { TicketThread } from '../components/ticket-thread.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })

/** What a history row says happened, from the status pair it recorded. */
function eventLabel(event: TaskEvent): string {
  if (event.fromStatus === null) return 'Created'
  if (event.fromStatus === event.toStatus) return 'Edited'
  if (event.toStatus === 'pending_review') return 'Marked done — awaiting review'
  if (event.fromStatus === 'pending_review') {
    return event.toStatus === 'done' ? 'Approved' : 'Sent back'
  }
  return event.toStatus === 'done' ? 'Completed' : 'Reopened'
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-2xs uppercase tracking-wide text-foreground-subtle">{label}</p>
      <div className="font-medium">{children}</div>
    </div>
  )
}

/** The internal reply thread (t1221): oldest first, with a composer for anyone who can write. */
function RepliesCard({ task, canWrite }: { task: TaskDetail; canWrite: boolean }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [body, setBody] = useState('')
  const uploads = useFileUploads({ endpoint: '/attachments?for=task' })

  const { data: replies } = useQuery({
    queryKey: queryKeys.tasks.replies(task.id),
    queryFn: () => api.get<TaskReply[]>(`/tasks/${task.id}/replies`),
    refetchInterval: 15_000,
  })

  const send = useMutation({
    mutationFn: () =>
      api.post(`/tasks/${task.id}/replies`, { body: body.trim(), attachmentIds: uploads.ids }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.tasks.replies(task.id) })
      setBody('')
      uploads.reset()
      toast.success('Reply posted')
    },
    onError: (error) =>
      toast.error(
        'Could not post the reply',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const who = task.assignedStaffName ? `${task.assignedStaffName} and ` : ''
  return (
    <Card>
      <CardHeader>
        <CardTitle>Replies</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {(replies ?? []).length === 0 ? (
          <p className="text-foreground-muted">No replies yet.</p>
        ) : (
          <ol className="space-y-3">
            {(replies ?? []).map((reply) => (
              <li key={reply.id} className="rounded-md border border-border p-3">
                <p className="text-2xs text-foreground-muted">
                  <span className="font-medium text-foreground">{reply.authorName ?? 'Staff'}</span>{' '}
                  · {formatWhen(reply.createdAt)}
                </p>
                {reply.body ? (
                  <p className="mt-1 whitespace-pre-wrap">
                    <LinkifiedText text={reply.body} />
                  </p>
                ) : null}
                <AttachmentList files={reply.attachments} compact />
              </li>
            ))}
          </ol>
        )}
        {canWrite ? (
          <div className="space-y-2">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write a reply…"
              rows={3}
              maxLength={5000}
              aria-label="Reply"
              onPaste={uploads.handlePaste}
            />
            <AttachmentChips uploads={uploads} />
            <div className="flex items-center justify-between gap-3">
              <p className="text-2xs text-foreground-muted">
                {`${who}${task.createdByName ? `${task.createdByName} (who created it)` : 'its creator'} get notified.`}
              </p>
              <div className="flex items-center gap-1">
                <AttachButton uploads={uploads} />
                <Button
                  size="sm"
                  disabled={
                    (!body.trim() && uploads.ids.length === 0) ||
                    send.isPending ||
                    uploads.uploading
                  }
                  onClick={() => send.mutate()}
                >
                  Reply
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

const NONE = <span className="text-foreground-subtle">—</span>

/**
 * One task, read-only for anyone with `tasks:read`: the full description, who
 * did what and when, and what it is linked to. The edit, follow-up and
 * complete controls only show for `tasks:write` — the API enforces that too.
 */
export function TaskDetailPage() {
  const { taskId } = useParams({ from: '/authed/tasks/$taskId' })
  const { can } = useAuth()
  const canWrite = can('tasks:write')
  const canReview = can('tasks:review')
  const [editing, setEditing] = useState<TaskDetail | null>(null)
  const [completing, setCompleting] = useState<TaskDetail | null>(null)
  const [followUpOf, setFollowUpOf] = useState<TaskDetail | null>(null)
  const toggle = useTaskToggle()

  // Colleagues complete shared tasks while this is open — poll like the list.
  const {
    data: task,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.tasks.detail(taskId),
    queryFn: () => api.get<TaskDetail>(`/tasks/${taskId}`),
    refetchInterval: 15_000,
  })

  const { data: events } = useQuery({
    queryKey: queryKeys.tasks.events(taskId),
    queryFn: () => api.get<TaskEvent[]>(`/tasks/${taskId}/events`),
    enabled: Boolean(task),
    refetchInterval: 15_000,
  })

  const back = (
    <div className="pb-3">
      <Link
        to="/tasks"
        className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" aria-hidden="true" /> All tasks
      </Link>
    </div>
  )

  if (error instanceof ApiError && error.status === 404) {
    return (
      <Page>
        {back}
        <EmptyState
          icon={ListChecks}
          title="Task not found"
          description="It may have been removed, or the link is wrong."
        />
      </Page>
    )
  }

  if (isLoading || !task) {
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-64 w-full" />
      </Page>
    )
  }

  return (
    <Page>
      {back}
      <PageHeader
        title={task.title}
        description={`${task.taskNumber}${
          task.createdByName ? ` · created by ${task.createdByName}` : ''
        } · ${formatWhen(task.createdAt)}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <MarkImportantButton itemType="task" itemId={taskId} />
            <Badge tone={TASK_STATUS_TONE[task.status] ?? 'neutral'}>
              {humanizeStatus(task.status)}
            </Badge>
            {canWrite ? (
              <>
                <Button size="sm" variant="secondary" onClick={() => setEditing(task)}>
                  <Pencil className="size-3.5" aria-hidden="true" /> Edit
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setFollowUpOf(task)}>
                  <Plus className="size-3.5" aria-hidden="true" /> Follow-up
                </Button>
                {task.status === 'pending_review' && canReview ? (
                  <ReviewActions task={task} />
                ) : (
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={toggle.isPending}
                    onClick={() =>
                      task.status === 'open'
                        ? setCompleting(task)
                        : toggle.mutate({ id: task.id, action: 'reopen' })
                    }
                  >
                    {task.status === 'open' ? 'Complete' : 'Reopen'}
                  </Button>
                )}
              </>
            ) : null}
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {task.details ? (
                <p className="whitespace-pre-wrap">
                  <LinkifiedText text={task.details} />
                </p>
              ) : (
                <p className="text-foreground-muted">No description.</p>
              )}
              <AttachmentList files={task.attachments} />
              {task.status !== 'open' && task.completionNote ? (
                <div className="border-t border-border pt-3">
                  <p className="text-2xs uppercase tracking-wide text-foreground-subtle">
                    Completion note
                  </p>
                  <p className="whitespace-pre-wrap">
                    <LinkifiedText text={task.completionNote} />
                  </p>
                </div>
              ) : null}
            </CardContent>
          </Card>

          {task.supportTicketId ? (
            <Card>
              <CardHeader>
                <CardTitle>From the ticket</CardTitle>
              </CardHeader>
              <CardContent>
                <TicketThread ticketId={task.supportTicketId} />
              </CardContent>
            </Card>
          ) : null}
          <RepliesCard task={task} canWrite={canWrite} />

          <Card>
            <CardHeader>
              <CardTitle>History</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {(events ?? []).map((event) => (
                  <li key={event.id} className="flex gap-3 text-sm">
                    <span
                      className="mt-1.5 size-1.5 shrink-0 rounded-full bg-border-strong"
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <p className="font-medium">{eventLabel(event)}</p>
                      <p className="text-2xs text-foreground-muted">
                        {event.actorLabel ?? event.actorType} · {formatWhen(event.createdAt)}
                      </p>
                      {event.note ? (
                        <p className="mt-0.5 whitespace-pre-wrap text-xs text-foreground-muted">
                          <LinkifiedText text={event.note} />
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>People</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Detail label="Created by">
                {task.createdByName ?? NONE}
                <span className="block text-xs font-normal text-foreground-muted">
                  {formatWhen(task.createdAt)}
                </span>
              </Detail>
              <Detail label="Assigned to">
                {task.assignedStaffId ? (
                  task.assignedStaffName
                ) : (
                  <Badge tone="neutral">Everyone</Badge>
                )}
              </Detail>
              {task.startDate ? (
                <Detail label="Starts">
                  {formatDueDate(task.startDate)}
                  {isScheduled(task) ? (
                    <span className="block text-xs font-normal text-foreground-muted">
                      Hidden from task lists until then
                    </span>
                  ) : null}
                </Detail>
              ) : null}
              <Detail label="Due">
                <DueDate task={task} />
              </Detail>
              {task.status !== 'open' ? (
                <Detail
                  label={task.status === 'pending_review' ? 'Marked done by' : 'Completed by'}
                >
                  {task.completedByName ?? NONE}
                  {task.completedAt ? (
                    <span className="block text-xs font-normal text-foreground-muted">
                      {formatWhen(task.completedAt)}
                    </span>
                  ) : null}
                </Detail>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Linked to</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Detail label="Ticket">
                {task.supportTicketId ? (
                  <Link
                    to="/support-tickets/$ticketId"
                    params={{ ticketId: task.supportTicketId }}
                    className="font-identifier text-primary hover:underline"
                  >
                    {task.supportTicketNumber}
                  </Link>
                ) : (
                  NONE
                )}
              </Detail>
              <Detail label="Rental">
                {task.rentalId ? (
                  <Link
                    to="/rentals/$rentalId"
                    params={{ rentalId: task.rentalId }}
                    className="font-identifier text-primary hover:underline"
                  >
                    {task.rentalNumber}
                  </Link>
                ) : (
                  NONE
                )}
              </Detail>
              <Detail label={task.customers.length > 1 ? 'Customers' : 'Customer'}>
                {task.customers.length > 0 ? (
                  <div className="flex flex-col items-start">
                    {task.customers.map((c) => (
                      <Link
                        key={c.id}
                        to="/customers/$customerId"
                        params={{ customerId: c.id }}
                        className="text-primary hover:underline"
                      >
                        {c.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  NONE
                )}
              </Detail>
              <Detail label="Vendor">
                {task.vendorId ? (
                  <Link
                    to="/vendors/$vendorId"
                    params={{ vendorId: task.vendorId }}
                    className="text-primary hover:underline"
                  >
                    {task.vendorName}
                  </Link>
                ) : (
                  NONE
                )}
              </Detail>
              {task.parentTaskId ? (
                <Detail label="Follow-up of">
                  <Link
                    to="/tasks/$taskId"
                    params={{ taskId: task.parentTaskId }}
                    className="inline-flex items-center gap-1 font-identifier text-primary hover:underline"
                  >
                    <CornerDownRight className="size-3.5" aria-hidden="true" />
                    {task.parentTaskNumber}
                  </Link>
                </Detail>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      <CompleteTaskDialog task={completing} onClose={() => setCompleting(null)} />
      <EditTaskDialog task={editing} onOpenChange={(open) => !open && setEditing(null)} />
      <NewTaskDialog
        open={followUpOf !== null}
        onOpenChange={(open) => !open && setFollowUpOf(null)}
        parentTask={followUpOf}
      />
    </Page>
  )
}
